import { useEffect, useState, useRef } from "react";
import { NxtbnDrawer } from "../../../components";
import { NXSearch } from "../../../icons";
import { NXRightArrow } from "../../../icons";
import { InputField } from "../../common";
import { useLazyQuery, gql } from "@apollo/client";
import { formatCurrency } from "../../common/utils";


const GET_PRODUCTS = gql`
  query MyQuery(
    $last: Int, 
    $id: ID,
    $search: String, 
    $name: String, 
    $variantAlias: String, 
    $variantId: String, 
    $variantSku: String, 
    $variantName: String
  ) {
    products(
      first: 3, 
      last: $last, 
      id: $id,
      search: $search, 
      name: $name, 
      variantAlias: $variantAlias, 
      variantId: $variantId, 
      variantSku: $variantSku, 
      variantName: $variantName
    ) {
      edges {
        node {
          dbId
          name
          productThumbnailXs
          allVariants {
            price
            dbId
             variantThumbnailXs
            name
            alias
          }
        }
      }
    }
  }
`;

function ProductChooser({
  isOpen,
  onClose,
  parentSearchTerm,
  filterOn,
  onAdd,
}: {
  isOpen: boolean;
  onClose: () => void;
  parentSearchTerm: string;
  filterOn: string;
  onAdd: (variant: any) => void;
}) {
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [selectedVariants, setSelectedVariants] = useState<Map<number, any>>(new Map());
  const [currentFilterOn, setCurrentFilterOn] = useState<string>("");
  const [isFilterOpen, setIsFilterOpen] = useState<boolean>(false);
  const [selectedFilter, setSelectedFilter] = useState<any>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [getProducts, { data, loading, error }] = useLazyQuery(GET_PRODUCTS, {
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    setCurrentFilterOn(filterOn);
    setSearchQuery(parentSearchTerm);
  }, [filterOn, parentSearchTerm]);

  const toggleProductSelection = (product: any) => {
    const newSelectedVariants = new Map(selectedVariants);
    const productVariants = product.allVariants;
    const hasSelectedVariant = productVariants.some((v: any) => selectedVariants.has(v.dbId));

    if (hasSelectedVariant) {
      productVariants.forEach((v: any) => newSelectedVariants.delete(v.dbId));
    } else {
      productVariants.forEach((v: any) => {
        newSelectedVariants.set(v.dbId, {
          ...v,
          variantName: v.name ? `${v.name} #${v.dbId}` : `${product.name} #${v.dbId}`,
        });
      });
    }

    setSelectedVariants(newSelectedVariants);
  };

  const toggleVariantSelection = (product: any, variant: any) => {
    const newSelectedVariants = new Map(selectedVariants);
    const variantWithName = {
      ...variant,
      variantName: variant.name ? variant.name : `${product.name} #${variant.dbId}`,
    };

    if (newSelectedVariants.has(variant.dbId)) {
      newSelectedVariants.delete(variant.dbId);
    } else {
      newSelectedVariants.set(variant.dbId, variantWithName);
    }
    setSelectedVariants(newSelectedVariants);
  };

  const isProductSelected = (product: any) => {
    return product.allVariants.some((v: any) => selectedVariants.has(v.dbId));
  };

  const titles: Record<string, string> = {
    products: "All Products",
    popular_products: "Popular Products",
    collections: "Collections",
    product_type: "Product Types",
    tags: "Tags",
    vendors: "Vendors",
  };

  const filterOptions = [
    {id: 'id', label: 'Product ID'},
    { id: 'variantId', label: 'Variant ID' },
    { id: 'variantAlias', label: 'Variant Alias' },
    { id: 'variantSku', label: 'SKU' },
    { id: 'variantName', label: 'Variant Title' },
    { id: 'name', label: 'Product Title' },
  ];

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    if (selectedFilter) return;

    if (e.target.value.length > 0) {
      getProducts({ variables: { search: e.target.value } });

      if (!currentFilterOn) {
        setCurrentFilterOn("products");
      }
    }
  };

  const handleFilter = (e:any) =>  {
    e.preventDefault();
    getProducts({ variables: { [selectedFilter.id]: searchQuery } });
  }

  useEffect(() => {
    if (isOpen && searchRef.current) {
      searchRef.current.focus();
    }
  }, [isOpen]);

  const onCloseDirectionClick = () => {
    setCurrentFilterOn('');
    setSelectedFilter('');
  };

  return (
    <NxtbnDrawer
      title={titles[currentFilterOn] || 'Select Products'}
      isOpen={isOpen}
      onClose={onClose}
      closeDirection={titles[currentFilterOn] ? 'left' : 'none'}
      onCloseDirectionClick={onCloseDirectionClick}
    >
      <div className="flex flex-col h-full bg-gray-50">
        {/* Search Section */}
        <div className="p-4 bg-white border-b">
          <div className="relative">
            <NXSearch className="absolute top-1/2 left-4 transform -translate-y-1/2 text-gray-400" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search products"
              onChange={handleSearch}
              value={searchQuery}
              className="w-full p-3 pl-12 bg-gray-50 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-100 focus:border-primary-300 transition-all"
            />
            <button
              type="button"
              className="absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1.5 text-sm font-medium text-primary-600 bg-primary-50 rounded-md hover:bg-primary-100 transition-colors duration-200 flex items-center gap-2"
              onClick={() => setIsFilterOpen(!isFilterOpen)}
            >
              <svg className="w-4 h-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-.293.707L13 10.414V15a1 1 0 01-.553.894l-3 1.5A1 1 0 018 16.5v-6.086L3.293 6.707A1 1 0 013 6V4z" clipRule="evenodd" />
              </svg>
              Filter
            </button>
          </div>

          {/* Filter Dropdown */}
          {isFilterOpen && (
            <div className="absolute right-4 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 z-20">
              {filterOptions.map((option) => (
                <label key={option.id} className="flex items-center p-3 cursor-pointer hover:bg-gray-50 transition-colors">
                  <InputField
                    type="radio"
                    name="filter"
                    value={option.id}
                    checked={selectedFilter?.id === option?.id}
                    onChange={() => setSelectedFilter(option)}
                    className="w-4 h-4 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="ml-3 text-sm text-gray-700">{option?.label}</span>
                </label>
              ))}
            </div>
          )}

          {/* Selected Filter Tag */}
          {selectedFilter && (
            <div className="mt-3 flex items-center gap-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm bg-primary-50 text-primary-700">
                {selectedFilter.label}
                <button
                  onClick={() => setSelectedFilter(null)}
                  className="ml-2 text-primary-600 hover:text-primary-800"
                >
                  ×
                </button>
              </span>
            </div>
          )}

          {selectedFilter && (
            <div className="flex justify-center mt-3">
              <button
                type="button"
                onClick={handleFilter}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-500 transition-colors"
              >
                Filter with {selectedFilter.label}
              </button>
            </div>
          )}
        </div>

        {/* Main Content */}
        <div className="flex-1 overflow-y-auto">
          {!currentFilterOn ? (
            <nav className="p-4">
              {Object.entries(titles).map(([key, title]) => (
                <button
                  key={key}
                  onClick={() => setCurrentFilterOn(key)}
                  className="w-full flex items-center justify-between p-4 mb-2 bg-white rounded-lg hover:bg-gray-50 transition-colors duration-200"
                >
                  <span className="text-gray-700 font-medium">{title}</span>
                  <NXRightArrow className="w-5 h-5 text-gray-400" />
                </button>
              ))}
            </nav>
          ) : (
            <div className="p-4 space-y-4">
              {data?.products?.edges?.map(({ node: product }: any) => (
                <div
                  key={product.dbId}
                  className={`rounded-lg border shadow-sm transition-all duration-200 ${
                    isProductSelected(product) ? 'bg-primary-50 border-primary-200' : 'bg-white border-gray-200'
                  }`}
                >
                  {/* Product Header */}
                  <div className="p-4">
                    <div className="flex items-center gap-4">
                      <InputField
                        type="checkbox"
                        checked={isProductSelected(product)}
                        onChange={() => toggleProductSelection(product)}
                        className="w-5 h-5 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                      />
                      <img
                        src={product.productThumbnailXs}
                        alt={product.name}
                        className="w-16 h-16 object-cover rounded-lg border border-gray-200"
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="text-lg font-medium text-gray-900 truncate">{product.name}</h3>
                        <div className="flex items-center gap-4 mt-1">
                          <span className="text-sm text-gray-600">{product.allVariants.length} Variants</span>
                          <span className="text-lg font-semibold text-primary-600">{formatCurrency(product.allVariants[0]?.price)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Variants Section */}
                  {product.allVariants.length > 0 && (
                    <div className="border-t border-gray-200 bg-gray-50 rounded-b-lg">
                      <div className="p-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-3">Variants</h4>
                        <div className="space-y-3">
                          {product.allVariants.map((variant: any) => (
                            <div
                              key={variant.dbId}
                              className={`flex items-center gap-4 p-3 rounded-lg transition-colors ${
                                selectedVariants.has(variant.dbId) ? 'bg-primary-50' : 'bg-white'
                              }`}
                            >
                              <InputField
                                type="checkbox"
                                checked={selectedVariants.has(variant.dbId)}
                                onChange={() => toggleVariantSelection(product, variant)}
                                className="w-4 h-4 text-primary-600 rounded border-gray-300 focus:ring-primary-500"
                              />
                              <img
                                src={variant. variantThumbnailXs || product.productThumbnailXs}
                                alt={variant.name}
                                className="w-12 h-12 object-cover rounded-md border border-gray-200"
                              />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {variant.name ? `${variant.name} #${variant.dbId}` : `${product.name} #${variant.dbId}`}
                                </p>
                                <div className="flex items-center gap-4 mt-1">
                                  <span className="text-sm font-medium text-primary-600">{formatCurrency(variant.price)}</span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-white p-4">
          <div className="flex items-center justify-between">
            <span className="text-sm text-gray-600">
              {selectedVariants.size} variants selected
            </span>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => onAdd(Array.from(selectedVariants.values()))}
                disabled={selectedVariants.size === 0}
                className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                  selectedVariants.size === 0
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-primary-600 text-white hover:bg-primary-700"
                }`}
              >
                Add Selected
              </button>
            </div>
          </div>
        </div>
      </div>
    </NxtbnDrawer>
  );
}

export default ProductChooser;
